import httpRequestMethodEnum from "./enums/httpRequestMethod";
import httpContentTypeEnum from "./enums/httpContentType";
import imageVersionEnum from "./enums/imageVersion";
import fetchModeEnum from "./enums/fetchMode";
import { apiEndpointEnum } from "./enums/apiEndpoints";
import { apiHeadersEnum } from "./enums/apiHeaders";
import { baseURLEnum } from "./enums/baseURL";
import { columbiaAPI } from "./columbiaAPI";
import { baselineJWTExpiredWrapper, getBaselineJWT } from "./libJWT";
import { wrappedTypeResponse } from "./enums/wrappedTypeResponse";
import { baseURL, logger } from "../baselineApis";

const convertToFormData = (formDataElementsObject) => {
  const formData = new FormData();
  Object.keys(formDataElementsObject).forEach((elementKey) =>
    formData.append(elementKey, formDataElementsObject[elementKey])
  );

  return formData;
};

function createBearerHeader(authToken) {
  return `Bearer ${authToken}`;
}

/**
 * Method: POST
 * @param  {string}/{list} imageId or list of imageId strings.
 * @param  {string} image_info_version
 * @param  {string} preview_version
 * @param  {boolean} includeDeletedImages default is false
 */
async function _fetchById(
  imageId,
  organization,
  image_info_version,
  preview_version,
  includeDeletedImages = false
) {
  return getBaselineJWT().then((authToken) => {
    let parameters = {};

    if (image_info_version) {
      parameters[imageVersionEnum.IMAGE_INFO_VERSION] = image_info_version;
    }

    if (preview_version) {
      parameters[imageVersionEnum.PREVIEW_VERSION] = preview_version;
    }

    if (includeDeletedImages) {
      parameters[imageVersionEnum.DELETED] = includeDeletedImages;
    }

    let url = `${baseURL(baseURLEnum.IMAGE_ACCESS)}/${apiEndpointEnum.FETCH}`;
    logger.info({
      url: url,
      params: parameters,
      function: _fetchById.name,
    });

    let headers = {
      authorization: createBearerHeader(authToken),
      [apiHeadersEnum.CONTENT_TYPE]: httpContentTypeEnum.APPLICATION_JSON,
      accept: "*/*",
    };

    if (organization && organization.length) {
      headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
    }

    if (typeof imageId === "string") {
      imageId = [imageId];
    }

    const body = JSON.stringify(imageId);

    url = new URL(url);
    for (let key in parameters) {
      url.searchParams.append(key, parameters[key]);
    }

    const request = new Request(url, {
      method: httpRequestMethodEnum.POST,
      body: body,
      headers: headers,
      mode: fetchModeEnum.CORS,
    });

    return fetch(request);
  });
}

async function _fetchColumbiaInfoById(id, organization) {
  return getBaselineJWT().then((authToken) => {
    const url = `${baseURL(baseURLEnum.IMAGE_ACCESS)}/${apiEndpointEnum.FETCH}/${apiEndpointEnum.COLUMBIA_INFO}/${id}`;
    const headers = {
      authorization: createBearerHeader(authToken),
      [apiHeadersEnum.CONTENT_TYPE]: httpContentTypeEnum.APPLICATION_JSON,
      accept: "*/*",
    };

    if (organization && organization.length) {
      headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
    }

    const request = new Request(url, {
      method: httpRequestMethodEnum.GET,
      headers: headers,
      mode: fetchModeEnum.CORS,
    });

    return fetch(request);
  });
}

/**
 * Method: DELETE
 * @param  {string} imageId
 */
async function _deleteById(imageId, organization) {
  return getBaselineJWT().then((authToken) => {
    const apiEndpoint = httpRequestMethodEnum.DELETE.toLowerCase();

    const headers = {
      authorization: createBearerHeader(authToken),
      [apiHeadersEnum.CONTENT_TYPE]: httpContentTypeEnum.APPLICATION_JSON,
      accept: "*/*",
    };

    if (organization && organization.length) {
      headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
    }

    const url = `${baseURL(baseURLEnum.IMAGE_ACCESS)}/${apiEndpoint}/${imageId}`;
    logger.info({
      url: url,
      imageId: imageId,
      function: _deleteById.name,
    });
    const request = new Request(url, {
      method: httpRequestMethodEnum.DELETE,
      headers: headers,
      mode: fetchModeEnum.CORS,
    });

    return fetch(request);
  });
}

/**
 * Method: POST
 * @param  {string} sso_token
 */
async function _transferOwnership(sso_token) {
  return getBaselineJWT().then((authToken) => {
    const apiEndpoint = apiEndpointEnum.REASSIGN_OWNERSHIP
    const headers = {
      authorization: sso_token,
      "Legacy-Token": authToken,
      [apiHeadersEnum.CONTENT_TYPE]: httpContentTypeEnum.APPLICATION_JSON,
      accept: "*/*",
    };

    const url = `${baseURL(baseURLEnum.IMAGE_ACCESS)}/${apiEndpoint}`;
    logger.info({
      url: url,
      function: _transferOwnership.name,
    });

    const request = new Request(url, {
      method: httpRequestMethodEnum.POST,
      headers: headers,
      mode: fetchModeEnum.CORS,
    });

    return fetch(request);
  });
}

/**
 * Method: POST
 * @param  {number[]} imageIdList
 * @param  {string} organization
 * @param  {boolean} includeDeletedImages default is false
 */
async function _fetchByMultipleIds(imageIdList, organization, includeDeletedImages = false) {
  return getBaselineJWT().then((authToken) => {
    const apiEndpoint = apiEndpointEnum.FETCH;
    const url = new URL(`${baseURL(baseURLEnum.IMAGE_ACCESS)}/${apiEndpoint}`);
    let headers = {
      authorization: createBearerHeader(authToken),
      [apiHeadersEnum.CONTENT_TYPE]: httpContentTypeEnum.APPLICATION_JSON,
      accept: "*/*",
    };

    if (organization && organization.length) {
      headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
    }

    if (includeDeletedImages) {
      url.searchParams.append(imageVersionEnum.DELETED, includeDeletedImages);
    }

    const logHeaders = { ...headers };
    delete logHeaders.authorization;
    logger.info({
      url: url,
      imageIdList: imageIdList,
      function: _fetchByMultipleIds.name,
      headers: logHeaders,
    });

    const request = new Request(url, {
      method: httpRequestMethodEnum.POST,
      headers: headers,
      body: JSON.stringify(imageIdList),
      mode: fetchModeEnum.CORS,
    });

    return fetch(request);
  });
}

/**
 * Method: POST
 * @param  {number[]} imageIdList
 */
async function _fetchPreviews(imageIdList, organization) {
  return getBaselineJWT().then((authToken) => {
    const apiEndpoint = apiEndpointEnum.FETCH_PREVIEW;
    const url = `${baseURL(baseURLEnum.IMAGE_ACCESS)}/${apiEndpoint}`;
    let headers = {
      authorization: createBearerHeader(authToken),
      [apiHeadersEnum.CONTENT_TYPE]: httpContentTypeEnum.APPLICATION_JSON,
      accept: "*/*",
    };

    if (organization && organization.length) {
      headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
    }

    const logHeaders = { ...headers };
    delete logHeaders.authorization;
    logger.info({
      url: url,
      imageIdList: imageIdList,
      function: _fetchPreviews.name,
      headers: logHeaders,
    });

    const request = new Request(url, {
      method: httpRequestMethodEnum.POST,
      headers: headers,
      body: JSON.stringify(imageIdList),
      mode: fetchModeEnum.CORS,
    });

    return fetch(request);
  });
}

/**
 * Method: GET
 * no params/inputs
 * @returns JSON format with user dates and query strings
 */
async function _sidebarDatesService(organization) {
  return getBaselineJWT().then((authToken) => {
    const url = `${baseURL(baseURLEnum.IMAGE_ACCESS)}/${apiEndpointEnum.TOP_LEVEL_INDEX}`;
    const timezone = new Intl.DateTimeFormat("en-US").resolvedOptions()
      .timeZone;
    let headers = {
      authorization: createBearerHeader(authToken),
      [apiHeadersEnum.USER_TIMEZONE]: timezone,
    };

    if (organization && organization.length) {
      headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
    }

    logger.info({
      url: url,
      function: _sidebarDatesService.name,
      method: httpRequestMethodEnum.GET,
    });

    const request = new Request(url, {
      method: httpRequestMethodEnum.GET,
      headers: headers,
      mode: fetchModeEnum.CORS,
    });

    return fetch(request);
  });
}

/**
 * Method: GET
 * no params/inputs
 * @returns JSON format with user dates and query strings
 */
async function _getSessionID(organization) {
  return getBaselineJWT().then((authToken) => {
    const url = `${baseURL(baseURLEnum.VISOR_API)}/${apiEndpointEnum.CREATE_SESSION}/${apiEndpointEnum.WEBSITE}`;
    const timezone = new Intl.DateTimeFormat("en-US").resolvedOptions()
      .timeZone;
    const headers = {
      authorization: createBearerHeader(authToken),
      [apiHeadersEnum.USER_TIMEZONE]: timezone,
    };

    if (organization && organization.length) {
      headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
    }

    logger.info({
      url: url,
      function: _getSessionID.name,
      method: httpRequestMethodEnum.GET,
    });

    const request = new Request(url, {
      method: httpRequestMethodEnum.GET,
      headers: headers,
      mode: fetchModeEnum.CORS,
    });

    return fetch(request);
  });
}
/**
 * Method: GET
 * @param  {
 * search_string: string,
 * sort_field: string,
 * sort_direction: string,
 * search_id: string,
 * page_size: int,
 * page: int,
 * } searchValues
 */
async function _fetchBySearch(
  searchValues = {
    search_string: "",
    sort_field: "",
    sort_direction: "",
    search_id: "",
    page_size: 20,
    page: 0,
  },
  organization
) {
  return getBaselineJWT().then((authToken) => {
    let url = `${baseURL(baseURLEnum.IMAGE_ACCESS)}/${apiEndpointEnum.SEARCH}`;
    const headers = {
      authorization: createBearerHeader(authToken),
    };

    if (organization && organization.length) {
      headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
    }

    logger.info({
      url: url,
      function: _fetchBySearch.name,
      method: httpRequestMethodEnum.GET,
      params: searchValues,
    });

    url = new URL(url);
    for (let key in searchValues) {
      let value = searchValues[key];
      switch (key) {
        case "search_string":
          if (value == "null" || value == null) {
            value = "";
          }
          break;
      }
      url.searchParams.append(key, value);
    }

    const request = new Request(url, {
      method: httpRequestMethodEnum.GET,
      headers: headers,
      mode: fetchModeEnum.CORS,
    });

    return fetch(request);
  });
}

async function _fetchImageDataJSON(jsonURL) {
  const request = new Request(jsonURL, {
    method: httpRequestMethodEnum.GET,
  });
  return fetch(request).then((response) => {
    return response.json();
  });
}

async function _fetchImageIRData(irDataURL) {
  const request = new Request(irDataURL, {
    method: httpRequestMethodEnum.GET,
  });
  return fetch(request).then((response) => {
    return response.arrayBuffer();
  });
}

async function _fetchOrganizationId() {
  return columbiaAPI.user.getOrganizations().then((organizations) => {
    return organizations?.data[0]?.id;
  });
}

/**
 * Method: POST
 * @param { string } imageId
 * @param  {
 * status: Number,
 * reason_code: Number,
 * previewImage: File,
 * jsonData: File,
 * } dataToStore
 */
async function _storeUpdatedThermalImage(id, dataToStore, organization) {
  return getBaselineJWT().then((authToken) => {
    const formData = convertToFormData(dataToStore);
    const url = `${baseURL(baseURLEnum.IMAGE_ACCESS)}/${apiEndpointEnum.STORE}/${id}`;
    const headers = {
      authorization: createBearerHeader(authToken),
      accept: "*/*",
    };

    if (organization && organization.length) {
      headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
    }

    const logHeaders = { ...headers };
    delete logHeaders.authorization;

    logger.info({
      url: url,
      imageId: id,
      function: imageAPI?.storeUpdatedThermalImages?.name,
      method: httpRequestMethodEnum.POST,
      data: formData,
      headers: logHeaders,
    });

    const request = new Request(url, {
      method: httpRequestMethodEnum.POST,
      body: formData,
      headers: headers,
      mode: fetchModeEnum.CORS,
    });

    return fetch(request);
  });
}

async function _fetchImageURLById(id, organization) {
  // returns presigned url and version
  const authToken = await getBaselineJWT();

  const url = `${baseURL(baseURLEnum.COLUMBIA_INFO)}/${apiEndpointEnum.COLUMBIA_INFO}/${id}`;

  let headers = {
    authorization: createBearerHeader(authToken),
    [apiHeadersEnum.CONTENT_TYPE]: httpContentTypeEnum.APPLICATION_JSON,
    accept: "*/*",
  };

  if (organization && organization.length) {
    headers[apiHeadersEnum.ORGANIZATION_ID] = organization;
  }

  const request = new Request(url, {
    method: httpRequestMethodEnum.GET,
    headers: headers,
    mode: fetchModeEnum.CORS,
  });

  return fetch(request).then((response) => {
    return response
      .json()
      .then((jsonResponse) => {
        const { s3_key, version } = jsonResponse;
        return { url: s3_key, version };
      })
      .catch((exception) => {
        console.warn(exception);
        return { url: null, version: null };
      });
  });
}

export const imageAPI = {
  fetchById: baselineJWTExpiredWrapper(_fetchById, wrappedTypeResponse.JSON),
  fetchColumbiaInfoById: baselineJWTExpiredWrapper(
    _fetchColumbiaInfoById,
    wrappedTypeResponse.BLOB
  ),
  deleteById: baselineJWTExpiredWrapper(_deleteById, null),
  fetchByMultipleIds: baselineJWTExpiredWrapper(
    _fetchByMultipleIds,
    wrappedTypeResponse.JSON
  ),
  fetchPreviews: baselineJWTExpiredWrapper(
    _fetchPreviews,
    wrappedTypeResponse.JSON
  ),
  sidebarDatesService: baselineJWTExpiredWrapper(
    _sidebarDatesService,
    wrappedTypeResponse.JSON
  ),
  getSessionID: baselineJWTExpiredWrapper(
    _getSessionID,
    wrappedTypeResponse.TEXT
  ),
  fetchBySearch: baselineJWTExpiredWrapper(
    _fetchBySearch,
    wrappedTypeResponse.JSON
  ),
  storeUpdatedThermalImage: baselineJWTExpiredWrapper(
    _storeUpdatedThermalImage,
    null
  ),
  transferOwnership: baselineJWTExpiredWrapper(
    _transferOwnership,
    wrappedTypeResponse.JSON
  ),
  fetchImageDataJSON: _fetchImageDataJSON,
  fetchImageIRData: _fetchImageIRData,
  fetchOrganizationId: _fetchOrganizationId,
  fetchImageURLById: _fetchImageURLById,
};
